import type { JSX } from 'react';
import React from 'react';

import {
  getAspectRatioNotation,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';
import { CreativeThumbnail, RequirementsCard } from '@feathr/components';

/**
 * Renders a single Meta dimension item as either a standard item (image only)
 * or a MetaItem (image + video) depending on whether the dimension has a video counterpart.
 *
 * @param key - Dimension key (e.g. "1080x1080")
 * @returns JSX element or null if the key is not a valid Facebook dimension
 */
export function MetaDimensionItem(key: string): JSX.Element | null {
  const imageDimension = ValidFacebookDimensions[key];
  // Defensive: returns null for unknown dimension keys so
  // the component degrades gracefully if ValidFacebookDimensions changes.
  if (!imageDimension) {
    return null;
  }
  const { name, width, height } = imageDimension;
  const videoDimension = ValidFacebookVideoDimensions[key];

  const commonProps = {
    aspectRatio: getAspectRatioNotation(width / height),
    dimensions: key,
    itemName: name,
    key,
  };

  if (!videoDimension) {
    return (
      <RequirementsCard.Item
        {...commonProps}
        thumbnail={<CreativeThumbnail height={height} variant={'meta'} width={width} />}
      />
    );
  }

  const { width: videoWidth, height: videoHeight } = videoDimension;

  return (
    <RequirementsCard.MetaItem
      {...commonProps}
      imageThumbnail={<CreativeThumbnail height={height} variant={'meta'} width={width} />}
      videoThumbnail={
        <CreativeThumbnail
          height={videoHeight}
          isVideo={true}
          variant={'meta'}
          width={videoWidth}
        />
      }
    />
  );
}
